import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { MuiBox, MuiContainer, MuiRadio, MuiTypography } from 'theme/material-ui';
import { Spacing, FontWeight } from 'modules/styles/variables';
import Spacer from 'components/UI/Layout/Spacer';
import FlexBox from 'components/UI/Layout/FlexBox';
import Footer from 'components/common/Footer';

import { RootState, RootDispatch } from 'store/types';
import { CovidScreeningQuestionnaireSelector } from 'store/CovidScreening/selectors';
import { Questionnaire } from 'store/CovidScreening/types';
import { setQuestionnaire } from 'store/CovidScreening/actions';
import { getProfile } from 'store/profile/actions';
import { patientSelect } from 'modules/constants/covidScreening';
import { StyledScreen } from '../../../components/common/PatientLoader/styled';
import { CovidTestButton } from '../../CovidScreening/Components/CovidTestButton';
import useStyles from '../../CovidScreening/Components/CovidTestButton/useStyles';
import { CovidTitles } from 'lib/constants/covidScreening';
import { AnalyticsEvent } from 'services/AnalyticsService';
import useNavigationAnalytics from 'hooks/useNavigationAnalytics';

interface CovidInsuranceProps extends RouteComponentProps {
  dispatch: RootDispatch;
  questionnaire: Questionnaire;
  setHasInsurance: typeof setQuestionnaire;
}

const CovidInsurance = ({ questionnaire, setHasInsurance, history }: CovidInsuranceProps) => {
  const classes = useStyles();

  const [insuranceData, setInsuranceData] = useState({
    hasInsurance: '',
    haveBeenPatient: '',
    haveChangedInsurance: ''
  });

  /* eslint-disable @typescript-eslint/camelcase */
  const covidScreeningNextBtn = useNavigationAnalytics(AnalyticsEvent.CovidNext, {
    title: CovidTitles.HAVE_HEALTH_INSURANCE,
    has_insurance: insuranceData.hasInsurance === 'Yes' ? 'yes' : 'no'
  });

  const covidScreeningPreviousBtn = useNavigationAnalytics(AnalyticsEvent.CovidPrev, {
    title: CovidTitles.HAVE_HEALTH_INSURANCE
  });

  const onCancelClick = () => {
    covidScreeningPreviousBtn();
    history.goBack();
  };

  useEffect(() => {
    setInsuranceData(prevState => ({
      ...prevState,
      hasInsurance: questionnaire?.has_insurance,
      haveBeenPatient: questionnaire?.have_been_patient,
      haveChangedInsurance: questionnaire?.have_changed_insurance
    }));
  }, []);

  const handleSubmit = () => {
    covidScreeningNextBtn();
    /* eslint-disable @typescript-eslint/camelcase */
    const questionnaireObject = {
      ...questionnaire,
      // patientInsuranceExist: insuranceExits,
      has_insurance: insuranceData.hasInsurance,
      have_been_patient: insuranceData.haveBeenPatient,
      have_changed_insurance: insuranceData.haveChangedInsurance
    };
    setHasInsurance(questionnaireObject);
    if (insuranceData.hasInsurance === 'No') {
      // they should proceed to the patient information summary screen.
      history.push('/guest-summary-info');
    } else {
      if (['No', 'Unknown'].includes(insuranceData.haveBeenPatient)) {
        // They are directed to the Health insurance dropdown screen to search for their existing insurance
        history.push('/guest-insurance-search');
      }
      if (['Yes', 'Unknown'].includes(insuranceData.haveChangedInsurance)) {
        // they are taken to Health insurance dropdown screen to search for their existing insurance
        history.push('/guest-insurance-search');
      }
      if (insuranceData.haveChangedInsurance === 'No') {
        // they are then taken to the information summary screen
        history.push('/guest-summary-info');
      }
    }
  };

  const handleSelection = (selectedDataObj: object) => {
    let patientInsuranceData = {};
    if (selectedDataObj?.hasInsurance === 'No') {
      patientInsuranceData = { haveBeenPatient: '', haveChangedInsurance: '' };
    } else if (
      selectedDataObj?.haveBeenPatient === 'No' ||
      selectedDataObj?.haveBeenPatient === 'Unknown'
    ) {
      patientInsuranceData = { haveChangedInsurance: '' };
    }
    setInsuranceData(prevState => ({
      ...prevState,
      ...selectedDataObj,
      ...patientInsuranceData
    }));
  };

  const hasInsurance = insuranceData.hasInsurance === 'Yes';
  const haveBeenPatient =
    insuranceData.haveBeenPatient === 'Yes' && insuranceData.hasInsurance === 'Yes';

  const isButtonDisabled = () => {
    if (
      insuranceData?.hasInsurance === 'No' ||
      (insuranceData?.hasInsurance === 'Yes' &&
        (insuranceData.haveBeenPatient === 'No' || insuranceData.haveBeenPatient === 'Unknown'))
    )
      return false;
    if (insuranceData?.haveBeenPatient === 'Yes') return !insuranceData.haveChangedInsurance;
    return true;
  };

  const renderInsuranceExit = () => (
    <>
      <FlexBox
        testID="covid-insurance-exit"
        width="100%"
        hSpacing={Spacing.medium}
        vSpacing={Spacing.medium}
      >
        <MuiTypography fontWeight={FontWeight.bold}>Do you have health insurance?</MuiTypography>
        <Spacer size="small" />
        <MuiTypography>
          Insurance information is needed to bill for COVID-19 tests. Copayments and deductibles may
          apply based on insurance plan.
        </MuiTypography>
        <Spacer size="small" />
        <MuiTypography>
          If additional insurance verification is needed, you may receive a follow-up call.
        </MuiTypography>
        <Spacer size="small" />
        <FlexBox flexDirection="row" alignItems="center">
          <MuiRadio
            onChange={() => handleSelection({ hasInsurance: 'Yes' })}
            checked={insuranceData.hasInsurance === 'Yes'}
            data-testid="insurance-yes"
            value={insuranceData.hasInsurance}
            color="primary"
          />
          <MuiTypography>Yes</MuiTypography>
        </FlexBox>
        <FlexBox flexDirection="row" alignItems="center">
          <MuiRadio
            onChange={() => handleSelection({ hasInsurance: 'No' })}
            checked={insuranceData.hasInsurance === 'No'}
            data-testid="insurance-no"
            value={insuranceData.hasInsurance}
            color="primary"
          />
          <MuiTypography>No</MuiTypography>
        </FlexBox>
      </FlexBox>
      {hasInsurance ? (
        <FlexBox width="100%" hSpacing={Spacing.medium} vSpacing={Spacing.medium}>
          <Spacer size="small" />
          <MuiTypography>
            Have you been a patient at an Intermountain Healthcare facility previously?
          </MuiTypography>
          <Spacer size="small" />
          <FlexBox flexDirection="row" alignItems="center">
            <MuiRadio
              onChange={() => handleSelection({ haveBeenPatient: 'Yes' })}
              checked={insuranceData.haveBeenPatient === 'Yes'}
              data-testid="haveBeenPatien-yes"
              value={insuranceData.haveBeenPatient}
              color="primary"
            />
            <MuiTypography>Yes</MuiTypography>
          </FlexBox>
          <FlexBox flexDirection="row" alignItems="center">
            <MuiRadio
              onChange={() => handleSelection({ haveBeenPatient: 'No' })}
              checked={insuranceData.haveBeenPatient === 'No'}
              data-testid="haveBeenPatien-no"
              value={insuranceData.haveBeenPatient}
              color="primary"
            />
            <MuiTypography>No</MuiTypography>
          </FlexBox>
          <FlexBox flexDirection="row" alignItems="center">
            <MuiRadio
              onChange={() => handleSelection({ haveBeenPatient: 'Unknown' })}
              checked={insuranceData.haveBeenPatient === 'Unknown'}
              data-testid="haveBeenPatien-unkown"
              value={insuranceData.haveBeenPatient}
              color="primary"
            />
            <MuiTypography>Unknown</MuiTypography>
          </FlexBox>
        </FlexBox>
      ) : null}
      {haveBeenPatient ? (
        <FlexBox width="100%" hSpacing={Spacing.medium} vSpacing={Spacing.medium}>
          <Spacer size="small" />
          <MuiTypography>
            Have you changed insurances since you were last seen at an Intermountain facility?
          </MuiTypography>
          <Spacer size="small" />
          <FlexBox flexDirection="row" alignItems="center">
            <MuiRadio
              onChange={() => handleSelection({ haveChangedInsurance: 'Yes' })}
              checked={insuranceData.haveChangedInsurance === 'Yes'}
              data-testid="changedInsurance-yes"
              value={insuranceData.haveChangedInsurance}
              color="primary"
            />
            <MuiTypography>Yes</MuiTypography>
          </FlexBox>
          <FlexBox flexDirection="row" alignItems="center">
            <MuiRadio
              onChange={() => handleSelection({ haveChangedInsurance: 'No' })}
              checked={insuranceData.haveChangedInsurance === 'No'}
              data-testid="changedInsurance-no"
              value={insuranceData.haveChangedInsurance}
              color="primary"
            />
            <MuiTypography>No</MuiTypography>
          </FlexBox>
          <FlexBox flexDirection="row" alignItems="center">
            <MuiRadio
              onChange={() => handleSelection({ haveChangedInsurance: 'Unknown' })}
              checked={insuranceData.haveChangedInsurance === 'Unknown'}
              data-testid="changedInsurance-unkown"
              value={insuranceData.haveChangedInsurance}
              color="primary"
            />
            <MuiTypography>Unknown</MuiTypography>
          </FlexBox>
        </FlexBox>
      ) : null}
    </>
  );
  return (
    <>
      <StyledScreen>
        <MuiContainer maxWidth="lg">
          <Spacer />
          {renderInsuranceExit()}
          <Spacer size="small" />
        </MuiContainer>
      </StyledScreen>
      <MuiBox width={275} className={classes.steppersFooter}>
        <CovidTestButton
          fullWidth
          variant="contained"
          data-testid="insurance-next-button"
          onClick={handleSubmit}
          disabled={isButtonDisabled()}
        >
          Next
        </CovidTestButton>
        <CovidTestButton
          fullWidth
          variant="text"
          data-testid="insurance-previous-button"
          onClick={onCancelClick}
        >
          Previous
        </CovidTestButton>
      </MuiBox>
      <Footer
        headerText={patientSelect.GET_PATIENTS.HELPLINE.CovidInfo}
        link={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
        linkName={patientSelect.GET_PATIENTS.HELPLINE.CovidPatientSelectHelpline}
        helplineHours={patientSelect.GET_PATIENTS.HELPLINE.CovidHelplineHours}
        title={CovidTitles.HAVE_HEALTH_INSURANCE}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  questionnaire: CovidScreeningQuestionnaireSelector(state)
});
const mapDispatchToProps = {
  setHasInsurance: setQuestionnaire,
  fetchInsurance: getProfile
};

export default connect(mapStateToProps, mapDispatchToProps)(CovidInsurance);
